"use client"

import { useCallback, useEffect, useState } from "react"
import { useAuthStore } from "@/lib/auth-store"
import { resolveTagOverlay, type TagProposal } from "@/lib/tag-proposals"

/**
 * Real, shared tag-proposal state for one post (contentId = "site:postId").
 * Backs the "dispute an existing tag" flow in booru-detail-modal.tsx and the
 * DisputesPanel — the suggest-new-tag flow lives in SuggestedTagsPanel, which
 * fetches its own copy since it is usually mounted separately (a different
 * tab). Both hit the same /api/tag-proposals.
 */
export function useTagProposals(contentId: string | null | undefined) {
  const { user } = useAuthStore()
  const [proposals, setProposals] = useState<TagProposal[]>([])
  const [approveThreshold, setApproveThreshold] = useState(6)
  const [loaded, setLoaded] = useState(false)

  const refresh = useCallback(() => {
    if (!contentId) return
    fetch(`/api/tag-proposals?contentId=${encodeURIComponent(contentId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return
        setProposals(data.proposals ?? [])
        if (data.thresholds?.approve) setApproveThreshold(data.thresholds.approve)
      })
      .catch(() => {})
      .finally(() => setLoaded(true))
  }, [contentId])

  useEffect(() => {
    setLoaded(false)
    setProposals([])
    refresh()
  }, [refresh])

  const overlay = resolveTagOverlay(proposals)

  /** Pending removal proposals for a specific tag — the "dispute count" badge. */
  const disputeCountFor = useCallback(
    (tag: string) => proposals.filter((p) => p.kind === "remove" && p.status === "pending" && p.tag === tag).length,
    [proposals],
  )

  const isTagHidden = useCallback((tag: string) => overlay.hidden.includes(tag), [overlay])

  /** Propose (or vote up an existing proposal to) remove a tag that's wrong for this post. */
  const disputeTag = useCallback(
    async (tag: string, reason: string) => {
      if (!contentId) return { ok: false, error: "Missing content id" }
      if (!user) return { ok: false, error: "sign-in-required" }
      try {
        const res = await fetch("/api/tag-proposals", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contentId, tag, kind: "remove", reason }),
        })
        const data = await res.json()
        if (!res.ok && res.status !== 409) return { ok: false, error: data.error || "Failed" }
        refresh()
        return { ok: true, alreadyResolved: res.status === 409 }
      } catch (err: any) {
        return { ok: false, error: err.message || "Failed" }
      }
    },
    [contentId, user, refresh],
  )

  const vote = useCallback(
    async (proposalId: string, value: 1 | -1 | 0) => {
      if (!user) return { ok: false, error: "sign-in-required" }
      // Optimistic update so the button feels instant.
      setProposals((prev) => prev.map((p) => (p.id !== proposalId ? p : { ...p, myVote: value })))
      try {
        const res = await fetch(`/api/tag-proposals/${proposalId}/vote`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ vote: value }),
        })
        const data = await res.json()
        if (!res.ok) {
          refresh()
          return { ok: false, error: data.error || "Failed" }
        }
        if (data.proposal) setProposals((prev) => prev.map((p) => (p.id === proposalId ? data.proposal : p)))
        return { ok: true }
      } catch (err: any) {
        refresh()
        return { ok: false, error: err.message || "Failed" }
      }
    },
    [user, refresh],
  )

  return { proposals, overlay, approveThreshold, loaded, disputeCountFor, isTagHidden, disputeTag, vote, refresh }
}
